import type { HealthcareFilterProduct } from '@models/healthcare-filters';
import type { DoctorActivityStatusEnum } from '@models/ocs/doctor';

export type ClinicListItem = HealthcareFilterProduct;

export interface ClinicFilterOption {
  id: string;
  title: string;
}

export interface ClinicCity {
  id: number;
  name: string;
  centerCount?: number;
}

export interface ClinicInsuranceGroup {
  id: string;
  title: string;
  filterKey: 'basicInsurance' | 'supplementaryInsurance';
  options: ClinicFilterOption[];
}

export type GetClinicFilterOptionsResponse = {
  clinicTypes: ClinicFilterOption[];
  specialities: ClinicFilterOption[];
  services: ClinicFilterOption[];
  insuranceGroups: ClinicInsuranceGroup[];
};

export type GetClinicCitiesResponse = {
  cities: ClinicCity[];
};

export type ClinicSpecialty = {
  specialtyId: number;
  specialtyTitle: string;
  shortTitle: string | null;
  profileId: number;
};

export type UserServiceItem = {
  id: number;
  serviceHierarchyName: string;
  serviceHierarchyId: number;
  price: number;
  discountPercent: number;
  discountPrice: number;
  finalPrice: number;
  imageURL: string | null;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  deposit: number;
};

export type GetUserServicesByProfilePayload = {
  profileId: number;
  pageNumber?: number;
  pageSize?: number;
};

export type GetUserServicesByProfileResponse = {
  queryResult: UserServiceItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
};

export type GetCalendarManagementPayload = {
  id: number;
  userId?: number;
  dayOfWeek?: DayOfWeek;
  medicalCenterId?: number;
  serviceId?: number;
  pageNumber?: number;
  pageSize?: number;
  skipCount?: number;
};

export enum SlotStatus {
  Available = 1,
  Locked = 2,
  Reserved = 3,
  Disabled = 4,
}

export enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

export interface BookingReservePayload {
  ServiceId: number;
  ServiceName: string;
  providerId: number;
  patientPhoneNumber: string;
  patientName: string;
  date: string;
  fromTime: string;
  toTime: string;
  price: number;
  discountAmount: number;
  discountPercentage: number;
  serviceIds: number[];
  deposit: number;
}

export type CalendarTimeRange = {
  start: string;
  end: string;
};

export type CalendarWorkingDay = {
  dayOfWeek: DayOfWeek;
  dayTitle: string;
  timeRanges: CalendarTimeRange[];
};

export type CalendarSchedule = {
  id: number;
  medicalCenterId: number;
  serviceId: number;
  slotDurationMinutes: number;
  capacity: number;
  includeOfficialHolidays: boolean;
  visitType: number;
  visitTypeName: string;
  isActive: boolean;
  workingDays: CalendarWorkingDay[];
};

export type CalendarDaySlot = {
  id: number;
  scheduleId: number;
  medicalCenterId: number;
  serviceId: number;
  startTime: string;
  endTime: string;
  status: SlotStatus;
  statusName: string;
  capacity: number;
  reservedCount: number;
  availableCapacity: number;
};

export type CalendarDay = {
  date: string;
  persianDate: string;
  dayOfWeek: DayOfWeek;
  dayTitle: string;
  isOfficialHoliday: boolean;
  totalCapacity: number;
  reservedCount: number;
  availableCapacity: number;
  slots: CalendarDaySlot[];
};

export type GetCalendarManagementResponse = {
  id: number;
  userId: number;
  title: string;
  startDate: string;
  endDate: string;
  startDatePersian: string;
  endDatePersian: string;
  totalCapacity: number;
  reservedCount: number;
  schedules: CalendarSchedule[];
  days: CalendarDay[];
};

export interface ClinicInsuranceProvider {
  id: number;
  name: string;
  insuranceTypeId: number;
  insuranceTypeName: InsuranceTypeName;
  logoUrl?: string | null;
}

export type InsuranceTypeName = 'Basic' | 'Supplementary';

export type UserInsuranceItem = {
  id: number;
  insuranceId: number;
  insuranceName: string;
  insuranceTypeId: number;
  insuranceTypeName: InsuranceTypeName;
  isActive: boolean;
  createdAt: string;
  imageUrl: string | null;
};

export type GetUserInsurancesByProfilePayload = {
  profileId: number;
  pageNumber?: number;
  pageSize?: number;
};

export type GetUserInsurancesByProfileResponse = {
  queryResult: UserInsuranceItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
};

export type ClinicProfileBasicInfo = {
  fullName: string;
  description: string | null;
  coverImageUrl: string | null;
  rate: number;
};

export type ClinicProfileAddress = {
  recId: number;
  profileId: number;
  title: string;
  cityId: number;
  regionName: string | null;
  address: string;
  latitude: number;
  longitude: number;
  phoneNumber?: string | null;
};

export type ClinicProfileAttribute = {
  key: string;
  value: string;
};

export type ClinicProfileAttributes =
  | ClinicProfileAttribute[]
  | Record<string, string>
  | null
  | undefined;

export type ClinicProfile = {
  recId: number;
  ownerId: number;
  profileType: ProfileEnum;
  basicInfo: ClinicProfileBasicInfo;
  addresses: ClinicProfileAddress[];
  servicePresentations: unknown[];
  specialties: ClinicSpecialty[];
  attributes: ClinicProfileAttributes;
  availableVisitServices: unknown | null;
};

export type GetClinicProfileResponse = ClinicProfile;

export type ClinicPlaceDoctorPerson = {
  specialtyId: number;
  id: number;
  fullName: string;
  coverImageUrl: string | null;
};

export type ClinicPlaceSpecialty = {
  specialtyId: number;
  specialtyTitle: string;
  shortTitle: string | null;
  doctorsCount: number;
  persons: ClinicPlaceDoctorPerson[];
};

export type GetClinicPlaceDoctorsResponse = {
  specialitiesCount: number;
  placePersons: ClinicPlaceSpecialty[];
};

export type FilterDoctorsBySpecialityAndPlacePayload = {
  LatestSpecialityId: number;
  ProfileId: number;
  ticks?: number;
};

export type FilterDoctorsDto = {
  recId: number;
  id: string | null;
  medicalNumber: string | null;
  latestSpecialityName: string;
  latestSpecialityId: number;
  fullName: string;
  rate: number;
  imageUrl: string | null;
  activityStatus: DoctorActivityStatusEnum;
  educationLevelId: number;
  doctorReviewsCount: number | null;
  cities: string[] | null;
  hasInPersonCalendar: boolean;
  profileId: number | null;
};

export type FilterDoctorsBySpecialityAndPlaceResponse = FilterDoctorsDto[];

export enum ProfileEnum {
  DOCTOR = 1,
  CLINIC = 2,
  LABORATORY = 3,
}

export const ProfileEnumLabel: Record<ProfileEnum, string> = {
  [ProfileEnum.DOCTOR]: 'دکتر',
  [ProfileEnum.CLINIC]: 'کلینیک',
  [ProfileEnum.LABORATORY]: 'آزمایشگاه',
};

export enum InsuranceType {
  Basic = 1,
  Supplementary = 2,
}

export const InsuranceTypeLabel: Record<InsuranceType, string> = {
  [InsuranceType.Basic]: 'Basic',
  [InsuranceType.Supplementary]: 'Supplementary',
};

export const ClinicProfileAttributeKey = {
  clinicType: 'clinicType',
  installmentStatus: 'installmentStatus',
  supplementaryInsurance: 'acceptsSupplementaryInsurance',
} as const;

export const getClinicProfileAttribute = (
  attributes: ClinicProfileAttributes,
  key: string,
): string | undefined => {
  if (!attributes) {
    return undefined;
  }

  if (Array.isArray(attributes)) {
    return attributes.find((item) => item.key === key)?.value;
  }

  if (typeof attributes === 'object') {
    const value = attributes[key];
    return typeof value === 'string' ? value : undefined;
  }

  return undefined;
};

export type SearchClinicItem = {
  doctorParentSpecialityId: number;
  speciality: string;
  clinicName?: string | null;
  category?: string | null;
  root: string;
  level4: string;
  rate: number;
  specialityId: number;
  imageUrlLevel4: string | null;
  service: string;
  doctorRecId: number;
  slug: string | null;
  imageUrlLevel2: string | null;
  serviceId: number;
  imageUrlLevel3: string | null;
  slangs: string | null;
  level2: string;
  recId: number;
  level3: string;
  level1: number;
  cities: string;
  profileOwnerId?: number | null;
  profileId?: number | null;
  doctorsCount?: number | null;
  reviewCount?: number | null;
  cityName?: string | null;
  neighborhood?: string | null;
};

export type SearchClinicPayload = {
  search: string;
  pageNumber?: number;
  pageSize?: number;
};

export type SearchClinicResponse = {
  queryResult: SearchClinicItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
};

export type BookingHeaderProfile = {
  ownerId: number;
  profileType: ProfileEnum;
  basicInfo: Pick<
    ClinicProfile['basicInfo'],
    'fullName' | 'description' | 'coverImageUrl'
  >;
  addresses?: Pick<ClinicProfile['addresses'][number], 'address'>[];
};
